import { api } from "aldoo-ra/API";

// Create API client for customer-support service
const supportAPI = api("customer-support");

// api.js - Enhanced SubmitSupportTicket function with better error handling

/**
 * Send a support ticket to the server
 * @param {Object} ticketData - The ticket data
 * @returns {Promise<Object>} - API response
 */
export const SubmitSupportTicket = async (ticketData) => {
    try {
        // Create form data to handle file uploads
        const formData = new FormData();

        // Create the ticket payload
        const ticketPayload = {
            email: ticketData.email,
            description: ticketData.description,
            summary: ticketData.summary,
            tags: ticketData.tags,
            assignedTo: ticketData.assignedTo
        };

        // Add the ticket data as a JSON string with the appropriate command name
        formData.append(
            "submit_ticket",
            JSON.stringify(ticketPayload)
        );

        // Add files if any
        if (ticketData.files && ticketData.files.length > 0) {
            if (Array.isArray(ticketData.files)) {
                ticketData.files.forEach((file, index) => {
                    formData.append(`files[${index}]`, file);
                });
            } else {
                formData.append("files", ticketData.files);
            }
        }

        // Use the existing API client with the appropriate headers
        const response = await supportAPI(formData, {
            headers: { "Content-Type": "multipart/form-data" }
        });

        return response;
    } catch (error) {
        console.error("Failed to submit ticket:", error);
        throw error;
    }
};
/**
 * Get the list of support tickets
 * @param {Object} options - Query options
 * @returns {Promise<object>} - API response with tickets
 */
export const GetTickets = async (options = {}) => {
    try {
        const response = await supportAPI({
            get_tickets: {
                page: options.page || 1,
                itemsPerPage: options.itemsPerPage || 10,
                search: options.search,
                match: options.match,
                sort: options.sort || { timestamp: -1 },
            },
        });
        return response;
    } catch (error) {
        console.error("Failed to fetch tickets:", error);
        throw error;
    }
};

/**
 * Get detailed information about a specific ticket
 */
export const GetTicketDetails = async (ticketId) => {
    try {
        const response = await supportAPI({ get_ticket_details: { ticketId } });
        return response;
    } catch (error) {
        console.error("Failed to fetch ticket details:", error);
        throw error;
    }
};

/**
 * Add a message to an existing ticket
 */
export const AddTicketMessage = async ({ ticketId, body, files }) => {
    try {
        const formData = new FormData();
        formData.append(
            "add_message",
            JSON.stringify({ ticketId, body })
        );
        if (files) {
            if (Array.isArray(files)) files.forEach((f, i) => formData.append(`files[${i}]`, f));
            else formData.append("files", files);
        }
        const response = await supportAPI(formData, { headers: { "Content-Type": "multipart/form-data" } });
        return response;
    } catch (error) {
        console.error("Failed to add message to ticket:", error);
        throw error;
    }
};

/**
 * Send a message to an existing ticket (simplified version)
 * @param {Object} params - Message parameters
 * @param {string} params.ticketId - The ticket ID to send the message to
 * @param {string} params.body - The message body text
 * @param {string} params.author - The author ID or email
 * @param {string} [params.messageType] - The type of message ('message' or 'note')
 * @param {File|File[]} [params.files] - Optional file attachment(s)
 * @returns {Promise<object>} - API response
 */
export const SendMessage = async ({ ticketId, body, author, messageType = 'message', files }) => {
    try {
        const formData = new FormData();
        formData.append(
            "send_message",
            JSON.stringify({ ticketId, body, author, messageType })
        );

        if (files) {
            if (Array.isArray(files)) {
                files.forEach((file, index) => formData.append(`files[${index}]`, file));
            } else {
                formData.append("files", files);
            }
        }

        const response = await supportAPI(formData, {
            headers: { "Content-Type": "multipart/form-data" }
        });
        return response;
    } catch (error) {
        console.error("Failed to send message:", error);
        throw error;
    }
};

/**
 * Get messages for a specific ticket, optionally after a specific timestamp
 * @param {Object} params - Parameters
 * @param {string} params.ticketId - The ticket ID
 * @param {string|Date} [params.after] - Only return messages after this timestamp
 * @returns {Promise<Object>} - API response with messages
 */
export const GetMessages = async ({ ticketId, after }) => {
    try {
        // Make sure we have a valid ticketId
        if (!ticketId) {
            throw new Error("Ticket ID is required");
        }

        // Convert timestamp to ISO string if it exists
        const afterTimestamp = after ? new Date(after).toISOString() : null;

        // Create the API request
        const response = await supportAPI({
            get_messages: {
                ticketId,
                after: afterTimestamp
            }
        });

        return response;
    } catch (error) {
        console.error("Failed to fetch messages:", error);
        throw error;
    }
};

/**
 * Update an existing support ticket (status, tags, isSpam, etc.)
 * @param {Object} updateData - Update data
 * @param {string} updateData.ticketId - The ticket ID to update
 * @param {string} [updateData.status] - New status ('open' or 'resolved')
 * @param {string[]} [updateData.tags] - Array of tags
 * @param {boolean} [updateData.isSpam] - Flag indicating if ticket is spam
 * @returns {Promise<object>} - API response
 */
export const UpdateTicket = async (updateData) => {
    try {
        const response = await supportAPI({ update_ticket: updateData });
        return response;
    } catch (error) {
        console.error("Failed to update ticket:", error);
        throw error;
    }
};

/**
 * Assign a ticket to a given user
 */
export const UpdateTicketAssignment = async (ticketId, assigneeEmail) => {
    try {
        const response = await supportAPI({ assign_ticket: { ticketId, assignee: assigneeEmail } });
        return response;
    } catch (error) {
        console.error("Failed to assign ticket:", error);
        throw error;
    }
};

/**
 * Fetch all unique tags from all tickets
 * @returns {Promise<object>} - API response with array of tags
 */
export const GetTags = async () => {
    try {
        const response = await supportAPI({ get_tags: {} });
        return response;
    } catch (error) {
        console.error("Failed to fetch tags:", error);
        throw error;
    }
};

/**
 * Delete tickets(soft or hard)
    * @param { Array | string } ids - IDs of tickets to delete
 * @param { string } mode - 'soft' or 'hard' delete
 * @returns { Promise < Object >} Response with result
*/
export const DeleteTickets = async (ids, mode = 'soft') => {
    try {
        const response = await supportAPI({ delete_tickets: { ids, mode } });
        return response;
    } catch (error) {
        console.error("Failed to delete tickets:", error);
        throw error;
    }
};

/**
 * Restore deleted tickets
 * @param {Array|string} ids - IDs of tickets to restore
 * @returns {Promise<Object>} Response with result
 */
export const RestoreTickets = async (ids) => {
    try {
        const response = await supportAPI({ restore_tickets: { ids } });
        return response;
    } catch (error) {
        console.error("Failed to restore tickets:", error);
        throw error;
    }
};

/**
 * Get deleted tickets using the regular GetTickets function with match
 * @param {Object} options - Query options
 * @returns {Promise<Object>} Response with data, totalItems, totalPages
 */
export const GetDeletedTickets = async (options = {}) => {
    try {
        // Create a new options object that includes the deleted:true match
        const deletedOptions = {
            ...options,
            match: {
                ...(options.match || {}),
                deleted: true
            }
        };

        // Use the regular GetTickets function with the modified options
        return await GetTickets(deletedOptions);
    } catch (error) {
        console.error("Failed to fetch deleted tickets:", error);
        throw error;
    }
};

/**
 * Fetch support agents with pagination or all at once
 * @param {Object} options - Query options
 * @param {number} options.page - Page number for pagination
 * @param {number} options.itemsPerPage - Items per page for pagination
 * @param {string} options.search - Search term
 * @param {Object} options.match - Additional query filters
 * @param {Object} options.sort - Sort criteria
 * @returns {Promise<object>} - API response
 */
export const GetAgents = async (options = {}) => {
    try {
        // Determine if we should use pagination or fetch all
        const usesPagination = options.page !== undefined || options.itemsPerPage !== undefined;

        const params = {
            get_agents: {
                // If pagination parameters are provided, use them - otherwise use a high value to get all
                page: options.page || 1,
                itemsPerPage: usesPagination ? (options.itemsPerPage || 10) : 1000,
                search: options.search,
                match: options.match,
                sort: options.sort || { name: 1 },
            },
        };

        const response = await supportAPI(params);
        return response;
    } catch (error) {
        console.error("Failed to fetch agents:", error);
        throw error;
    }
};

/**
 * Add a new support agent
 * @param {Object} agentData - Agent data
 * @returns {Promise<object>} - API response
 */
export const AddAgent = async (agentData) => {
    try {
        const response = await supportAPI({
            add_agent: {
                email: agentData.email,
                name: agentData.name,
            },
        });
        return response;
    } catch (error) {
        console.error("Failed to add agent:", error);
        throw error;
    }
};

/**
 * Edit an existing agent's details
 * @param {Object} agentData - Agent data
 * @returns {Promise<object>} - API response
 */
export const EditAgent = async (agentData) => {
    try {
        const response = await supportAPI({
            edit_agent: {
                userId: agentData.userId,
                email: agentData.email,
                name: agentData.name,
                isAgent: agentData.isAgent,
                permissions: agentData.permissions,
                revokePermissions: agentData.revokePermissions,
            },
        });
        return response;
    } catch (error) {
        console.error("Failed to edit agent:", error);
        throw error;
    }
};

/**
 * Get customer support statistics
 * @returns {Promise<object>} - API response with support statistics
 */
export const GetStatistics = async () => {
    try {
        const response = await supportAPI({ get_statistics: {} });
        return response;
    } catch (error) {
        console.error("Failed to fetch support statistics:", error);
        throw error;
    }
};

/**
 * Merge multiple tickets into one primary ticket
 * @param {Object} mergeData - Merge data
 * @param {string} mergeData.primaryTicketId - The ID of the primary ticket
 * @param {Array<string>} mergeData.secondaryTicketIds - Array of IDs of tickets to merge into the primary ticket
 * @param {Object} mergeData.notes - Notes to add to the tickets
 * @param {string} mergeData.notes.primary - Note to add to the primary ticket
 * @param {string} mergeData.notes.secondary - Note to add to the secondary tickets
 * @returns {Promise<Object>} - API response
 */
export const MergeTickets = async (mergeData) => {
    try {
        const response = await supportAPI({
            merge_tickets: {
                primaryTicketId: mergeData.primaryTicketId,
                secondaryTicketIds: mergeData.secondaryTicketIds,
                notes: {
                    primary: mergeData.notes?.primary || '',
                    secondary: mergeData.notes?.secondary || ''
                }
            }
        });
        return response;
    } catch (error) {
        console.error("Failed to merge tickets:", error);
        throw error;
    }
};

export const GetRelayStatus = async () => {
    const response = await supportAPI({ relay_status: {} });
    return response;
};

export const TriggerRelaySetup = async () => {
    const response = await supportAPI({ relay_setup: {} });
    return response;
};

export default {
    SubmitSupportTicket,
    GetTickets,
    GetTicketDetails,
    AddTicketMessage,
    SendMessage,
    GetMessages,
    UpdateTicket,
    UpdateTicketAssignment,
    GetTags,
    DeleteTickets,
    RestoreTickets,
    GetDeletedTickets,
    GetAgents,
    AddAgent,
    EditAgent,
    GetStatistics,
    MergeTickets,
};